import { createFileRoute, Link } from '@tanstack/react-router'
import { format, subDays } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { Eye, EyeOff } from 'lucide-react'

import { BillingWarning } from '#components/features/billing/billing-access'
import { DashboardCards } from '#components/features/dashboard/dashboard-cards'
import { DashboardChart } from '#components/features/dashboard/dashboard-chart'
import { PageHeader } from '#components/layout/page-header'
import { WorkspaceLoadingSkeleton } from '#components/layout/workspace-loading-skeleton'
import { Button } from '#components/ui/button'
import { useAnalytics } from '#hooks/api/use-analytics'
import { useBillingAccess } from '#hooks/api/use-billing-access'
import { useAuth } from '#hooks/use-auth'
import { useBillingPrivacy } from '#hooks/use-billing-privacy'
import { hasBlockingQueryError } from '#utils/query-state'

export const Route = createFileRoute('/_auth/_admin/dashboard')({
  head: () => ({
    meta: [{ title: 'Visão geral | UseComanda' }],
  }),
  component: RouteComponent,
})

function RouteComponent() {
  const { tenantId } = useAuth()
  const billing = useBillingAccess(tenantId)
  const dashboard = useAnalytics({
    enabled: !billing.isLoading && billing.accessState.canOperate,
  })
  const { isBillingHidden, toggleBillingVisibility } = useBillingPrivacy()

  const yesterdayDate = format(subDays(new Date(), 1), "d 'de' MMMM", {
    locale: ptBR,
  })

  if (billing.isLoading) {
    return <WorkspaceLoadingSkeleton />
  }

  if (!billing.accessState.canOperate) {
    return (
      <div className="p-6">
        <div className="border-destructive/20 bg-destructive/5 mx-auto max-w-3xl rounded-xl border p-6">
          <h1 className="text-xl font-semibold">{billing.accessState.title}</h1>
          <p className="text-muted-foreground mt-2 text-sm">
            {billing.accessState.description}
          </p>
          <div className="mt-5 flex gap-2">
            <Button asChild>
              <Link to="/planos">Regularizar plano</Link>
            </Button>
            <Button asChild variant="outline">
              <Link to="/historico/pedidos">Consultar histórico</Link>
            </Button>
          </div>
        </div>
      </div>
    )
  }

  if (dashboard.isLoading) return <WorkspaceLoadingSkeleton />

  if (
    hasBlockingQueryError(dashboard.isError, dashboard.data) ||
    !dashboard.data
  ) {
    return (
      <div className="grid min-h-64 place-items-center p-6 text-center">
        <div>
          <h1 className="text-xl font-semibold">
            Não foi possível carregar os dados
          </h1>
          <p className="text-muted-foreground mt-2 text-sm">
            Tente novamente em instantes.
          </p>
          <Button
            className="mt-4"
            variant="outline"
            onClick={() => void dashboard.refetch()}
          >
            Tentar novamente
          </Button>
        </div>
      </div>
    )
  }

  const getDashboardSummary = dashboard.data

  return (
    <main className="bg-muted/20 min-h-full px-4 py-6 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-6xl space-y-6">
        {billing.accessState.kind === 'warning' && (
          <BillingWarning state={billing.accessState} />
        )}
        <PageHeader
          eyebrow="Operação"
          title="Visão geral"
          subtitle={
            <>
              Resumo da operação de ontem,{' '}
              <span className="text-foreground font-medium">
                {yesterdayDate}
              </span>
              .
            </>
          }
          actions={
            <Button
              size="sm"
              variant="outline"
              onClick={toggleBillingVisibility}
            >
              {isBillingHidden ? <Eye /> : <EyeOff />}
              {isBillingHidden ? 'Mostrar valores' : 'Ocultar valores'}
            </Button>
          }
        />
        <DashboardCards
          yesterday_revenue={getDashboardSummary.yesterday_revenue}
          before_yesterday_revenue={
            getDashboardSummary.before_yesterday_revenue
          }
          yesterday_orders={getDashboardSummary.yesterday_orders}
          weekly_avg_orders={getDashboardSummary.weekly_avg_orders}
          ticket_avg={getDashboardSummary.ticket_avg}
          before_yesterday_ticket_avg={
            getDashboardSummary.before_yesterday_ticket_avg
          }
          best_selling_product={getDashboardSummary.best_selling_product}
          best_selling_product_qtd={
            getDashboardSummary.best_selling_product_qtd
          }
          hideBilling={isBillingHidden}
        />
        <DashboardChart
          data={getDashboardSummary.revenue_week}
          hideBilling={isBillingHidden}
        />
      </div>
    </main>
  )
}
